import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";

import useLocalStorage from "../../common/hooks/useLocalStorage";
import {
  getHubSpotAuthUrl,
  validateHubSpotAuthCode,
} from "../../services/firebase";
import { signedInUserAtom } from "../../state/atoms";
import { Errors } from "../../types/errors";
import { LOCAL_STORAGE_KEYS } from "../consts";

export const useConnectHubSpot = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");

  const [hubspotAuthCode, setHubspotAuthCode] = useLocalStorage(
    LOCAL_STORAGE_KEYS.hubspotAuthCode,
    ""
  );
  const [hubspotRedirectUri, setHubspotRedirectUri] = useLocalStorage(
    LOCAL_STORAGE_KEYS.hubspotRedirectUri,
    ""
  );
  const signedInUser = useRecoilValue(signedInUserAtom);

  useEffect(() => {
    const getConnectHubSpotUrl = async () => {
      // Only fetch URL if we don't have a code to process
      // This prevents unnecessary API calls when doing other OAuth flows
      if (hubspotAuthCode) {
        // We have a code, skip fetching URL - we'll process the code instead
        return;
      }
      
      try {
        setIsProcessing(true);
        setError("");

        // Get the redirect URI from window location and normalize it
        // Remove trailing slash to ensure consistency (backend will normalize too)
        const redirectUri = `${window.location.origin}/oauth/hubspot`.replace(/\/$/, '');
        setHubspotRedirectUri(redirectUri);

        const { result, status } = await getHubSpotAuthUrl(redirectUri);

        if (status === 200 && result?.hubspotAuthUrl) {
          setUrl(result.hubspotAuthUrl);
        } else {
          setError(
            `Error connecting to HubSpot (Code: ${Errors.CONNECT_OUTREACH_GENERATE_URL_FUNCTION_ERROR}). Please contact us for help.`
          );
        }
      } catch (error) {
        setError(
          `Error connecting to HubSpot (Code: ${Errors.CONNECT_OUTREACH_GENERATE_URL_FUNCTION_FAILURE}). Please contact us for help.`
        );
      } finally {
        setIsProcessing(false);
      }
    };

    getConnectHubSpotUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateHubSpotAccessToken = async () => {
      try {
        setIsProcessing(true);
        setError("");

        if (signedInUser && hubspotAuthCode) {
          // Normalize redirect URI to ensure it matches what was used in auth URL
          const redirectUri = (hubspotRedirectUri || `${window.location.origin}/oauth/hubspot`).replace(/\/$/, '');
          console.log("Validating HubSpot auth code:", { 
            hasCode: !!hubspotAuthCode, 
            redirectUri,
            userId: signedInUser.uid 
          });
          
          const { status, result } =
            await validateHubSpotAuthCode(hubspotAuthCode, redirectUri);
          
          console.log("HubSpot validation response:", { status, isAuthorized: result?.isHubSpotAuthorized });
          
          setHubspotAuthCode("");
          setHubspotRedirectUri("");
          if (status === 200 && result?.isHubSpotAuthorized) {
            console.log("HubSpot connected successfully");
            // Store organization in localStorage for state update
            try {
              localStorage.setItem(LOCAL_STORAGE_KEYS.pendingOrganization, JSON.stringify(result));
            } catch (error) {
              console.error("Failed to store pending organization:", error);
            }
            // Don't reload - let state update naturally
            // The App.tsx useEffect will pick up the organization changes
          } else {
            const errorMsg = `Error connecting to HubSpot. Status: ${status}. Please try again or contact support.`;
            console.error("HubSpot validation failed:", { status, result });
            setError(errorMsg);
          }
        }
      } catch (error: any) {
        console.error("HubSpot validation error:", error);
        // Show the actual error message from the backend
        const errorMsg = error?.message || error?.details || `Error connecting to HubSpot (Code: ${Errors.CONNECT_OUTREACH_UPDATE_ORG_FUNCTION_FAILURE}). Please contact us for help.`;
        console.error("Full error details:", {
          message: error?.message,
          code: error?.code,
          details: error?.details,
          stack: error?.stack,
        });
        setError(errorMsg);
      } finally {
        setIsProcessing(false);
      }
    };

    updateHubSpotAccessToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubspotAuthCode, signedInUser]);

  const openHubSpotAuthUrl = () => {
    if (url) {
      const width = 800; // Set the width of the popup
      const height = 800; // Set the height of the popup

      // Calculate the position to center the window
      const left = (window.innerWidth - width) / 2 + window.screenX;
      const top = (window.innerHeight - height) / 2 + window.screenY;

      const params = `width=${width},height=${height},scrollbars=yes,resizable=yes,top=${top},left=${left}`;

      const newWindow = window.open(url, "Authorize HubSpot", params);
      newWindow?.focus();
    }
  };

  return {
    isProcessing,
    url,
    error,
    openHubSpotAuthUrl,
  };
};
